import React from "react";
import Modal2 from "../Modal";
import { chainData } from "../../chains";
import { resolveChainRow, chainRowToChainId } from "../../lib/resolveChainRow";
import { useTokenRole } from "./useTokenRole";

/**
 * NftDetailModal — reusable NFT detail modal (image + traits + links + actions).
 *
 * Drawn from the collection page's MetaModal + Modal2 wiring, extracted so any
 * page (collection grid, wallet owned-NFT cards, etc.) can open the exact same
 * item-detail modal without duplicating the content markup.
 *
 * Controlled: parent sets `show` and passes the item + image. All callbacks are
 * optional/dep-ended by the consumer.
 *
 * @param {Object}            props
 * @param {boolean}           props.show         - open/close
 * @param {()=>void}          props.onClose      - close handler
 * @param {string}            props.title        - modal title
 * @param {Object}            props.item         - { meta, coll, tokId, ... }
 * @param {string}            [props.imageUrl]   - rendered image src (pre-resolved)
 * @param {number|string}     [props.chainIndex] - chainData index for chainSlug
 * @param {string}            [props.chainSlug]  - override chainSlug (else derived from chainIndex)
 * @param {boolean}           [props.noMeta]     - force "no traits" empty state
 * @param {()=>void}          [props.onTraitsSearch] - trait value click handler (after #someid set)
 * @param {({to:string})=>void}  [props.onNavigate]     - "go to item page" navigator
 * @param {(target:any)=>void}   [props.onBuy]          - Buy button handler (token has a live auction)
 * @param {(target:any)=>void}   [props.onSell]         - Sell button handler (owner, NOT listed)
 * @param {(target:any)=>void}   [props.onManage]       - Manage button handler (owner, already listed)
 */
const NftDetailModal = ({
  show,
  onClose,
  title,
  item,
  imageUrl,
  chainIndex,
  chainSlug,
  noMeta,
  onTraitsSearch,
  onNavigate,
  onBuy,
  onSell,
  onManage,
}: {
  show?: boolean;
  onClose?: () => void;
  title?: any;
  item?: any;
  imageUrl?: any;
  chainIndex?: any;
  chainSlug?: string;
  noMeta?: boolean;
  onTraitsSearch?: any;
  onNavigate?: any;
  onBuy?: (target: any) => void;
  onSell?: (target: any) => void;
  onManage?: (target: any) => void;
  ownerAddress?: string | null;
}) => {
  const meta = item?.meta || {};

  const resolvedChainRow = React.useMemo(
    () => resolveChainRow({ chainIndex, chainId: item?.chainId }),
    [chainIndex, item?.chainId]
  );
  const tokenChainId = chainRowToChainId(resolvedChainRow);

  // Which action this token offers, and therefore what the header button says.
  // Driven by on-chain ownership + listing state (see useTokenRole), not by
  // the backend's cached auctionID:
  //     not ours + listed -> "Buy"      ours + listed     -> "Manage"
  //     not ours + unlisted-> no button  ours + unlisted   -> "Sell"
  // An owner seeing "Buy" on their own item is the bug this replaces.
  const { role, loading: roleLoading } = useTokenRole({
    contractAddress: item?.contractAddress,
    tokenId: item?.tokId,
    chainId: tokenChainId,
    auctionIdHint: item?.aid ?? item?.auctionID,
  });

  // The target handed to whichever action handler fires. One shape for all
  // three, so onBuy/onSell/onManage stay interchangeable at the call site.
  const actionTarget = {
    auctionId: Number(item?.aid ?? item?.auctionID ?? 0) || 0,
    contractAddress: item?.contractAddress,
    tokenId: item?.tokId,
    chainIndex,
    chainId: tokenChainId,
    collectionId: item?.coll,
    meta,
  };

  const actionLabel =
    role === 'buy' ? 'Buy' : role === 'sell' ? 'Sell' : role === 'manage' ? 'Manage' : null;

  const handleAction = () => {
    if (role === 'buy') onBuy?.(actionTarget);
    else if (role === 'sell') onSell?.(actionTarget);
    else if (role === 'manage') onManage?.(actionTarget);
  };

  const resolvedChainSlug =
    chainSlug ||
    (chainIndex !== undefined
      ? String(chainData?.[chainIndex]?.tokName || chainData?.[chainIndex]?.name || "sgb").toLowerCase()
      : "sgb");

  const hasTraits = !noMeta && Array.isArray(meta?.attributes) && meta.attributes.length > 0;

  const [imgFailed, setImgFailed] = React.useState(false);
  const hasImage = !!imageUrl && !imgFailed;

  const handleTraitClick = (value) => {
    if (!onTraitsSearch) return;
    const search = document.getElementById("someid");
    if (search) search.value = value;
    onTraitsSearch(value);
  };

  const content = (
    <>
      <div style={{ position: 'relative', marginBottom: '12px', borderRadius: 'var(--card-radius, 12px)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
          {hasImage ? (
            <img
              src={imageUrl}
              alt={meta?.name || 'NFT'}
              style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain', objectPosition: 'center', maxHeight: 'calc(85vh - 140px)' }}
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="pixel-collection-placeholder is-delayed" style={{ minHeight: '180px', minWidth: '33vw', aspectRatio: '1 / 1' }} />
          )}
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', padding: '12px', background: 'linear-gradient(transparent, rgba(12,16,32,0.95))' }}>
            <div style={{ maxHeight: 'calc(85vh - 280px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px', lineHeight: 1.25 }}>
              {!hasTraits ? (
                <div style={{ textAlign: 'center', color: '#aeb6c8', padding: '16px 0' }}>
                  This item has no traits defined, or we don't have a record of them. Click "go to item page" and we will attempt to fetch them.
                </div>
              ) : (
                meta?.attributes?.map((_item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span className="text-meta-border">{_item.trait_type}</span>
                    <span className="text-meta-border">
                      {String(_item?.value ?? '').includes("://") ? (
                        <a rel="noopener noreferrer" href={_item.value} target="_blank" style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'none' }}>{_item.value}</a>
                      ) : (
                        <span style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.88)' }} onClick={() => handleTraitClick(_item.value)}>{_item.value}</span>
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      <div style={{ textAlign: 'center', margin: '12px 0', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {meta?.external_url && (
          <a href={meta.external_url} style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'underline', fontSize: '0.9rem' }}>Website</a>
        )}
        {meta?.youtube_url && (
          <a href={meta.youtube_url} style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'underline', fontSize: '0.9rem' }}>YouTube</a>
        )}
        {meta?.animation_url && (
          <a href={meta.animation_url} style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'underline', fontSize: '0.9rem' }}>Media</a>
        )}
      </div>

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {item?.ownerAddress && (
          <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <span style={{ color: '#aeb6c8', fontSize: '0.9rem' }}>Owner: </span>
            <a
              href={'/wallet/' + encodeURIComponent(item.ownerAddress)}
              style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'underline', fontSize: '0.9rem' }}
            >
              {item.ownerAddress.length > 10
                ? `${item.ownerAddress.slice(0, 6)}...${item.ownerAddress.slice(-4)}`
                : item.ownerAddress}
            </a>
          </div>
        )}
        {onNavigate && (
          <button
            className="pixel-btn"
            style={{ width: '100%' }}
            onClick={() => {
              onClose?.();
              const path = meta?.edition
                ? `/item/${resolvedChainSlug}/${item?.coll}/${meta.edition}`
                : `/item/${resolvedChainSlug}/${item?.coll}/${item?.tokId}`;
              onNavigate({ to: path });
            }}
          >
            Go To Item Page
          </button>
        )}
        <button
          className="pixel-btn"
          style={{ width: '100%', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#aeb6c8' }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </>
  );

  return (
    <Modal2
      show={show}
      modalTitle={title}
      content={content}
      handleClose={onClose}
      titleAction={
        // Hidden entirely while the ownership read is in flight, so the header
        // never flashes "Buy" at an owner before the chain says otherwise.
        actionLabel && !roleLoading ? (
          <button
            type="button"
            className="pixel-modal-buy"
            onClick={handleAction}
            data-auction-id={actionTarget.auctionId || undefined}
            data-token-role={role}
          >
            {actionLabel}
          </button>
        ) : null
      }
    />
  );
};

export default NftDetailModal;